import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import { SkeletonCom } from '../../shared/utils/SkeletonCom';
import CustomDivider from '../../shared/utils/CustomDivider';
import {
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Grid2,
    IconButton,
    Paper,
    Stack,
    Button,
    useTheme,
    useMediaQuery,

} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { getFileUrl } from '../../shared/constants';
import VideoPlayer from '../../shared/components/proteacher/VideoPlayer';
import { useGetActivityLmsById } from '../../assets/nodeApi/getApi/getAdminApi';
import MainDialogBox from '../../shared/components/Dialog/MainDialogBox';
import { useDialog } from '../../shared/hook/useDialog';
import EditActivityLms from '../../features/activelms/EditActivityLms';
import AddIcon from '@mui/icons-material/Add';
import CustomPagination from '../../shared/utils/CustomPagination';


// Helper function to normalize Windows local folder paths to standard relative paths
const cleanFilePath = (filePath) => {
    if (!filePath) return "";
    // If it is already a web URL, return it
    if (/^https?:\/\//i.test(filePath)) return filePath;

    // Normalize backslashes to forward slashes
    let normalized = filePath.replace(/\\/g, '/');

    // Check if it contains 'uploads/' and slice it from there
    const uploadsIndex = normalized.indexOf('uploads/');
    if (uploadsIndex !== -1) {
        return '/' + normalized.substring(uploadsIndex);
    }
    return normalized;
};

const ViewAllLmsContant = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // Fetch the courseId passed in the router parameter
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const gradeId = queryParams.get('gradeId') || id;

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

    // Call API with parameter object { courseId, search }
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const videoModalHeight = isMobile ? 220 : isTablet ? 300 : 420;

    const { data: activityLmsData, isLoading } = useGetActivityLmsById({ 
        courseId: id, 
        gradeId: gradeId,
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize
    });

    const [expandedTopic, setExpandedTopic] = useState(null);
    const [expandedActivity, setExpandedActivity] = useState(null);
    const { open: openFile, handleClickOpen: handleOpenFile, handleClose: handleCloseFile } = useDialog();
    const { open: openVideo, handleClickOpen: handleOpenVideo, handleClose: handleCloseVideo } = useDialog();
    const { open: openEdit, handleClickOpen: handleOpenEditDialog, handleClose: handleCloseEdit } = useDialog();
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [isAddMode, setIsAddMode] = useState(false);
    const [selectedTopicId, setSelectedTopicId] = useState(null);

    const handleOpenEdit = (activity) => {
        setIsAddMode(false);
        setSelectedActivity(activity);
        handleOpenEditDialog();
    };

    const handleOpenAdd = (topicId) => {
        setIsAddMode(true);
        setSelectedTopicId(topicId);
        setSelectedActivity(null);
        handleOpenEditDialog();
    };

    const handleFileClick = (file) => {
        if (file.file_type === 'pdf' || (file.file_path && file.file_path.toLowerCase().endsWith('.pdf'))) {
            const cleanPath = cleanFilePath(file.file_path);
            const fileUrl = cleanPath.startsWith('http') ? file.file_path : `${window._env_.IMAGE_PATH_NODE}${cleanPath}`;
            window.open(fileUrl, '_blank');
        } else {
            setSelectedFile(file);
            handleOpenFile();
        }
    };

    const handleVideoClick = (video) => {
        setSelectedVideo(video);
        handleOpenVideo();
    };

    const activities = Array.isArray(activityLmsData?.data) ? activityLmsData?.data : (activityLmsData?.data?.data || activityLmsData?.data?.records || []);
    const firstActivity = activities[0];
    const courseTitle = firstActivity?.course?.title || firstActivity?.course_name || "LMS Activities";
    const gradeName = firstActivity?.grade?.name || firstActivity?.grade_name || "N/A";
    const subjectId = firstActivity?.course?.subject_id || firstActivity?.subject_id || firstActivity?.subject?.id || firstActivity?.course?.subject?.id;

    // Group activities by topic
    const groupedActivities = activities.reduce((acc, curr) => {
        const topicId = curr.topic?.id || 'unassigned';
        const topicName = curr.topic?.name || 'Unassigned Topic';

        if (!acc[topicId]) {
            acc[topicId] = {
                id: topicId,
                name: topicName,
                display_order: curr.topic?.display_order || 9999,
                activities: []
            };
        }
        acc[topicId].activities.push(curr);
        return acc;
    }, {});

    const sortedTopics = Object.values(groupedActivities).sort((a, b) => a.display_order - b.display_order);

    // Sort activities within each topic by ID ascending
    sortedTopics.forEach(topic => {
        topic.activities.sort((a, b) => a.id - b.id);
    });
    const handleTopicChange = (topicId) => {
        setExpandedTopic(expandedTopic === topicId ? null : topicId);
    };

    const handleActivityChange = (activityId) => {
        setExpandedActivity(expandedActivity === activityId ? null : activityId);
    };

    const assumedTotal = (activities?.length === paginationModel.pageSize) 
        ? ((paginationModel.page + 1) * paginationModel.pageSize) + 1 
        : (paginationModel.page * paginationModel.pageSize) + (activities?.length || 0);

    const totalRows = activityLmsData?.pagination?.totalElements 
        || activityLmsData?.pagination?.total 
        || activityLmsData?.total 
        || activityLmsData?.data?.total 
        || activityLmsData?.totalRecords 
        || activityLmsData?.count
        || activityLmsData?.data?.count
        || assumedTotal;

    // Construct course header metadata cards
    const courseDetail = {
        "Course Title": courseTitle,
        "Grade": gradeName,
        "Total Activities": totalRows,
        "Status": firstActivity?.course?.status || "Active"
    };

    return (
        <NormalContainerWithHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/admin/activitylms/view')}
                    sx={{
                        mr: 3,
                        color: "#000",
                        px: 2,
                        fontWeight: 500,
                    }}
                >
                    Back to List
                </Button>
            </Box>

            {/* Loader Skeleton State */}
            {isLoading && (
                <Box sx={{ padding: "1rem" }}>
                    {[...Array(5)].map((_, index) => (
                        <Box key={index}>
                            <SkeletonCom height={50} style={{ borderRadius: "5px" }} />
                            <CustomDivider size={"1rem"} />
                        </Box>
                    ))}
                </Box>
            )}

            {/* Empty State */}
            {!isLoading && activities.length === 0 && (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                    <Typography variant="h6" >
                        No activities found for this course.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/admin/activitylms/view')}
                        sx={{ mt: 2, textTransform: 'none', borderRadius: 2 }}
                    >
                        Go Back
                    </Button>
                </Box>
            )}

            {/* Main Content Area */}
            {!isLoading && activities.length > 0 && (
                <>
                    {/* Header Metadata Grid */}
                    <Grid2 container spacing={2} sx={{ mb: 3 }}>
                        {Object.entries(courseDetail).map(([key, value]) => (
                            <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={key}>
                                <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                                    <Typography variant="caption" fontWeight={500}>
                                        {key}
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, textTransform: key === 'Status' ? 'capitalize' : 'none' }}>
                                        {value}
                                    </Typography>
                                </Paper>
                            </Grid2>
                        ))}
                    </Grid2>

                    <CustomDivider size="1rem" />

                    {/* Accordion List representing Topics and Activities */}
                    {sortedTopics.map((topic, topicIndex) => (
                        <Accordion
                            key={topic.id}
                            expanded={expandedTopic === topic.id}
                            onChange={() => handleTopicChange(topic.id)}
                            sx={{ ...styles.accordionTopic, mb: 3 }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
                                sx={styles.accordionTopicSummary}
                            >
                                <Typography variant="h5" fontWeight={500} color="#fff">
                                    Topic {paginationModel.page * paginationModel.pageSize + topicIndex + 1}: {topic.name}
                                </Typography>
                            </AccordionSummary>

                            <AccordionDetails sx={styles.accordionTopicDetails}>
                                {topic.activities.map((activity, index) => (
                                    <Box key={activity.id} sx={{ mb: 2 }}>
                                        <Accordion
                                            expanded={expandedActivity === activity.id}
                                            onChange={() => handleActivityChange(activity.id)}
                                            sx={styles.accordionActivity}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: '#4169E1' }} />}
                                                sx={styles.accordionSummary}
                                            >
                                                <Typography variant="body1" fontWeight={600} color="#333">
                                                    Activity {index + 1}: {activity.name}
                                                </Typography>
                                            </AccordionSummary>

                                            <AccordionDetails sx={styles.accordionDetails}>
                                                <Box sx={{ position: 'relative' }}>
                                                    {/* Edit Icon Button */}
                                                    <IconButton
                                                        sx={{ position: 'absolute', top: -16, right: -16, zIndex: 1, color: '#4169E1', bgcolor: 'rgba(65, 105, 225, 0.1)', '&:hover': { bgcolor: 'rgba(65, 105, 225, 0.2)' } }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenEdit(activity);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    {/* Stats Grid inside Accordion */}
                                                    <Grid2 container spacing={2} sx={{ mb: 3 }}>
                                                        <Grid2 size={{ xs: 12, sm: 4 }}>
                                                            <Box sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                                                <Typography variant="caption" display="block" fontWeight={500}>Duration</Typography>
                                                                <Typography variant="body2" fontWeight={600} color="#1e293b">{activity.duration || 'N/A'}</Typography>
                                                            </Box>
                                                        </Grid2>
                                                        <Grid2 size={{ xs: 12, sm: 3 }}>
                                                            <Box sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                                                <Typography variant="caption" display="block" fontWeight={500}>Age</Typography>
                                                                <Typography variant="body2" fontWeight={600} color="#1e293b">{activity.age || 'None'}</Typography>
                                                            </Box>
                                                        </Grid2>
                                                        <Grid2 size={{ xs: 12, sm: 4 }}>
                                                            <Box sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                                                <Typography variant="caption" display="block" fontWeight={500}>Materials Required</Typography>
                                                                <Typography variant="body2" fontWeight={600} color="#1e293b">{activity.material_required || 'None'}</Typography>
                                                            </Box>
                                                        </Grid2>
                                                    </Grid2>

                                                    {/* Description */}
                                                    {activity.description && (
                                                        <Box sx={{ mb: 3 }}>
                                                            <Typography variant="subtitle2" fontWeight={600} color="#444" gutterBottom sx={{ borderBottom: '1px solid #e2e8f0', pb: 1, mb: 2 }}>
                                                                Description
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6 }}>
                                                                {activity.description}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* Uploaded Files Section */}
                                                    {activity.uploaded_files && activity.uploaded_files.length > 0 && (
                                                        <Box sx={{ mb: 4 }}>
                                                            <Typography
                                                                variant="subtitle2"
                                                                fontWeight={600}
                                                                color="#333"
                                                                gutterBottom
                                                                sx={{ borderBottom: '1px solid #e2e8f0', pb: 1, mb: 2 }}
                                                            >
                                                                Uploaded Files & Attachments
                                                            </Typography>
                                                            <Grid2 container spacing={3}>
                                                                {activity.uploaded_files.map((file) => {
                                                                    const cleanPath = cleanFilePath(file.file_path);
                                                                    const fileUrl = cleanPath;
                                                                    return (
                                                                        <Grid2 size={{ xs: 4, sm: 2, md: 1.5 }} key={file.id}>
                                                                            <Paper sx={{ p: 0.2, boxShadow: 'none' }}>
                                                                                <Box onClick={() => handleFileClick(file)} sx={{ cursor: 'pointer' }}>
                                                                                    {file.file_type === 'image' ? (
                                                                                        <Box
                                                                                            component="img"
                                                                                            src={`${window._env_.IMAGE_PATH_NODE}${fileUrl}`}
                                                                                            alt={file.filename}
                                                                                            sx={{
                                                                                                width: { xs: 50, md: 75 },
                                                                                                height: { xs: 50, md: 75 },
                                                                                                objectFit: 'cover',
                                                                                                borderRadius: 1,
                                                                                                border: '1px solid #f1f5f9'
                                                                                            }}
                                                                                        />
                                                                                    ) : (
                                                                                        <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1, mb: 1.5 }}>
                                                                                            <Typography variant="h6" fontWeight={600}>
                                                                                                {file.file_type?.toUpperCase() || 'DOCUMENT'}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    )}
                                                                                </Box>
                                                                            </Paper>
                                                                        </Grid2>
                                                                    );
                                                                })}
                                                            </Grid2>
                                                        </Box>
                                                    )}

                                                    {/* Video Reference Section */}
                                                    {activity.video_urls && activity.video_urls.length > 0 && (
                                                        <Box>
                                                            <Typography
                                                                variant="subtitle2"
                                                                fontWeight={600}
                                                                color="#475569"
                                                                gutterBottom
                                                                sx={{ borderBottom: '1px solid #e2e8f0', pb: 1, mb: 2 }}
                                                            >
                                                                Video References
                                                            </Typography>
                                                            <Grid2 container spacing={3}>
                                                                {activity.video_urls.map((video) => (
                                                                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={video.id}>
                                                                        <Paper
                                                                            onClick={() => handleVideoClick(video)}
                                                                            sx={{
                                                                                p: 1,
                                                                                border: '1px solid #e2e8f0',
                                                                                boxShadow: 'none',
                                                                                borderRadius: 2,
                                                                                bgcolor: '#f8fafc',
                                                                                cursor: 'pointer',
                                                                                position: 'relative',
                                                                                overflow: 'hidden',
                                                                                '&:hover .play-overlay': { opacity: 1 }
                                                                            }}
                                                                        >
                                                                            <Box sx={{ pointerEvents: 'none' }}>
                                                                                <VideoPlayer url={video.url} height={130} play={false} />
                                                                            </Box>
                                                                            {/* Play icon overlay */}
                                                                            <Box
                                                                                className="play-overlay"
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    inset: 0,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    bgcolor: 'rgba(0,0,0,0.35)',
                                                                                    opacity: 0,
                                                                                    transition: 'opacity 0.2s',
                                                                                    borderRadius: 2,
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    sx={{
                                                                                        width: 48,
                                                                                        height: 48,
                                                                                        borderRadius: '50%',
                                                                                        bgcolor: 'rgba(255,255,255,0.9)',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                    }}
                                                                                >
                                                                                    <Box
                                                                                        sx={{
                                                                                            width: 0,
                                                                                            height: 0,
                                                                                            borderTop: '8px solid transparent',
                                                                                            borderBottom: '8px solid transparent',
                                                                                            borderLeft: '14px solid #4169E1',
                                                                                            ml: '3px',
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            </Box>
                                                                        </Paper>
                                                                    </Grid2>
                                                                ))}
                                                            </Grid2>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Box>
                                ))}
                            </AccordionDetails>
                            <Box sx={{ p: 2 }}>
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAdd(topic.id);
                                    }}
                                    sx={{ display: "flex", justifyContent: "center" }}
                                >
                                    <AddIcon /> <Typography>Add Activity</Typography>

                                </IconButton>
                            </Box>
                        </Accordion>
                    ))}
                    
                    {/* Pagination Component */}
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'flex-end', p: 1, gap: 2, mt: 2 }}>
                        <CustomPagination
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                            totalRows={totalRows}
                            pageSize={paginationModel.pageSize}
                        />
                    </Box>
                </>
            )}

            {/* Modal for viewing files */}
            {selectedFile && (
                <MainDialogBox open={openFile} handleClose={handleCloseFile} width="60%">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        {selectedFile.file_type === 'image' ? (
                            <Box
                                component="img"
                                src={cleanFilePath(selectedFile.file_path).startsWith('http') ? selectedFile.file_path : `${window._env_.IMAGE_PATH_NODE}${cleanFilePath(selectedFile.file_path)}`}
                                alt={selectedFile.filename}
                                sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                            />
                        ) : (
                            <Box sx={{ width: '100%', height: '70vh' }}>
                                <iframe
                                    src={cleanFilePath(selectedFile.file_path).startsWith('http') ? selectedFile.file_path : `${window._env_.IMAGE_PATH_NODE}${cleanFilePath(selectedFile.file_path)}`}
                                    title={selectedFile.filename}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 'none' }}
                                />
                            </Box>
                        )}
                    </Box>
                </MainDialogBox>
            )}

            {/* Modal for playing videos */}
            {selectedVideo && (
                <MainDialogBox open={openVideo} handleClose={handleCloseVideo} width="70%">
                    <Box sx={{ width: '100%' }}>
                        <VideoPlayer url={selectedVideo.url} height={videoModalHeight} play={true} />
                    </Box>
                </MainDialogBox>
            )}

            {/* Modal for editing/adding an activity */}
            {(selectedActivity || isAddMode) && (
                <MainDialogBox open={openEdit} handleClose={handleCloseEdit} width="65%">
                    <EditActivityLms
                        activityData={selectedActivity}
                        activityId={selectedActivity?.id}
                        isAddMode={isAddMode}
                        topicId={selectedTopicId}
                        subjectId={subjectId}
                        onClose={handleCloseEdit}
                    />
                </MainDialogBox>
            )}

            <Box sx={{ p: 2 }}>
                <IconButton
                    onClick={() => navigate('/admin/activitylms/add', { state: { eventId: firstActivity?.course?.event_id, gradeId: firstActivity?.grade?.id, subjectId: firstActivity?.subject?.id, page: paginationModel.page, limit: paginationModel.pageSize, topicIndex: paginationModel.page * paginationModel.pageSize + sortedTopics.length + 1 } })}
                    sx={{ display: "flex", justifyContent: "center" }}
                >
                    <AddIcon /> <Typography>Add Topic</Typography>

                </IconButton>
            </Box>
        </NormalContainerWithHeader>
    );
};

// Accordion Custom Theme Styling
const styles = {
    accordionTopic: {
        border: "1px solid #2563EB",
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        overflow: 'hidden',
        '&:before': {
            display: 'none',
        },
    },
    accordionTopicSummary: {
        backgroundColor: '#3B82F6',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        px: 3,
        py: 1.5,
        '&.Mui-expanded': {
            backgroundColor: '#2563EB',
        }
    },
    accordionTopicDetails: {
        p: 3,
        backgroundColor: '#F8FAFC'
    },
    accordionActivity: {
        border: "1px solid #4169E1",
        borderRadius: '8px',
        boxShadow: 'none',
        overflow: 'hidden',
        '&:before': {
            display: 'none',
        },
        '&.Mui-expanded': {
            boxShadow: '0px 4px 20px rgba(65, 105, 225, 0.08)',
        }
    },
    accordionSummary: {
        backgroundColor: '#F5F8FF',
        borderBottom: '1px solid rgba(65, 105, 225, 0.1)',
        px: 3,
        py: 1,
        '&.Mui-expanded': {
            backgroundColor: '#EBF1FF',
        }
    },
    accordionDetails: {
        p: 3,
        backgroundColor: '#ffffff'
    }
};

export default ViewAllLmsContant;
